// ContractItem.tsx
import { ConnectProto } from "@racoon/api";
import { cn } from "@racoon/ui";
import { useT, Namespace, TranslationKey } from "@racoon/i18n";
import { formatTimestampToDate } from "@racoon/lib";

interface ContractItemProps {
  contract: ConnectProto.Contract;
  onEditContract?: (contract: ConnectProto.Contract) => void;
}

export const ContractItem: React.FC<ContractItemProps> = ({ contract, onEditContract }) => {
  const t = useT();
  const isInactive = contract.status === ConnectProto.ContractStatus.INACTIVE;
  // Staged rows (create mode, not yet saved) use a "pending-N" sentinel id and
  // cannot be opened for editing — don't offer a click affordance that no-ops.
  const isPending =
    typeof contract.id === "string" && (contract.id as string).startsWith("pending-");

  const handleContractClick = () => {
    if (isPending) return;
    onEditContract?.(contract);
  };

  return (
    <div>
      {/* Contract Header */}
      <div
        onClick={handleContractClick}
        className={cn(
          "h-12 p-2 flex gap-2 items-center justify-between rounded-lg",
          !isPending && "cursor-pointer hover:bg-border-primary",
        )}
      >
        <div className="flex flex-[0.8] text-sm truncate">{contract.name}</div>
        <div className="flex flex-1 gap-2 items-center justify-end">
          <div className="flex gap-2 min-w-0">
            <span className="text-brand-blue text-sm font-medium whitespace-nowrap">№</span>
            <span className="text-sm max-w-14 truncate">{contract.dealNumber}</span>
          </div>
          <div>
            <span className="text-sm">от</span>
          </div>
          <div className="flex w-32 justify-start gap-1">
            <span className="w-auto text-sm">{formatTimestampToDate(contract.startAt)}</span>
          </div>
          <div>
            <div
              className={cn(
                "border-none text-xs leading-none w-20 text-center py-1 px-2 rounded-2xl",
                isInactive ? "bg-status-red text-brand-red" : "bg-status-green text-brand-green",
              )}
            >
              {contract.status === undefined
                ? t(Namespace.Enum, `CONTRACT_STATUS_PENDING` as TranslationKey)
                : t(
                    Namespace.Enum,
                    `CONTRACT_STATUS_${ConnectProto.ContractStatus[contract.status]}` as TranslationKey,
                  )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
